import baseFetch, { createParamString, getAccessToken } from 'common/baseFetch';
import { RateSurfSample } from 'types/rateTheSurf';
import type { Units } from 'types/units';
import addCustomUserAgentHeader from 'utils/addCustomUserAgentHeader';
import type {
  FetchConsistencyForecastParams,
  FetchCrowdForecastParams,
  FetchCrowdForecastResponse,
  FetchRegionalConditionForecastParams,
  FetchSwellsForecastParams,
  FetchTaxonomySwellEventsParams,
  FetchTideForecastParams,
  FetchSunlightTimesParams,
  FetchSwellSpectraForecastParams,
  FetchEnergyForecastParams,
  FetchWeatherForecastParams,
  FetchWindForecastParams,
  FetchSurfForecastParams,
  LiveSurfForecastResponse,
  FetchRatingForecastParams,
} from 'types/kbygApi';

// TODO: Replace type casting as any when adjusting related fetches to improve types

export const fetchSurfForecast = ({
  corrected,
  days,
  id,
  intervalHours,
  kbygQueryParams,
  noCache = false,
  startDate,
  units,
}: FetchSurfForecastParams) => {
  let cacheEnabled = !!units?.waveHeight;
  const headers: HeadersInit = {};
  const noCacheEnabled =
    noCache === true || (typeof noCache === 'string' && noCache.toLowerCase() === 'true');
  if (noCacheEnabled) {
    headers['Cache-Control'] = 'no-cache';
    cacheEnabled = false;
  }

  const queryParams: Record<string, any> = {
    ...kbygQueryParams,
    cacheEnabled,
    corrected,
    days,
    intervalHours,
    spotId: id,
    start: startDate,
    'units[waveHeight]': units?.waveHeight ?? null,
  };

  return baseFetch(`/kbyg/spots/forecasts/surf?${createParamString(queryParams)}`, {
    headers,
  });
};

interface FetchLiveSurfForecastParams {
  id: string;
  kbygQueryParams?: Record<string, string | boolean>;
  noCache?: boolean;
  units: Units;
}

export function fetchLiveSurfForecast({
  id,
  kbygQueryParams,
  noCache = false,
  units,
}: FetchLiveSurfForecastParams) {
  const headers = new Headers();
  if (noCache) {
    headers.set('Cache-Control', 'no-cache');
  }

  return baseFetch<LiveSurfForecastResponse>(
    `/kbyg/spots/live/surf?${createParamString({
      ...kbygQueryParams,
      spotId: id,
      'units[waveHeight]': units?.waveHeight ?? null,
    })}`,
    {
      headers,
    },
  );
}

export const fetchSwellsForecast = ({
  corrected,
  days,
  id,
  intervalHours,
  kbygQueryParams,
  noCache = false,
  startDate,
  units,
}: FetchSwellsForecastParams) => {
  let cacheEnabled = !!units?.swellHeight;
  const headers: HeadersInit = {};
  if (noCache === true) {
    headers['Cache-Control'] = 'no-cache';
    cacheEnabled = false;
  }

  const queryParams: Record<string, any> = {
    ...kbygQueryParams,
    cacheEnabled,
    corrected,
    days,
    intervalHours,
    spotId: id,
    start: startDate,
    'units[swellHeight]': units?.swellHeight ?? null,
  };

  return baseFetch(`/kbyg/spots/forecasts/swells?${createParamString(queryParams)}`, {
    headers,
  });
};

export const fetchTideForecast = ({
  id,
  days,
  kbygQueryParams,
  startDate,
  units,
  noCache = false,
}: FetchTideForecastParams) => {
  let cacheEnabled = !!units?.tideHeight;
  const headers: HeadersInit = {};
  if (noCache === true) {
    headers['Cache-Control'] = 'no-cache';
    cacheEnabled = false;
  }
  return baseFetch(
    `/kbyg/spots/forecasts/tides?${createParamString({
      ...kbygQueryParams,
      spotId: id,
      days,
      start: startDate,
      cacheEnabled,
      'units[tideHeight]': units?.tideHeight ?? null,
    })}`,
    {
      headers,
    },
  );
};

export const fetchWeatherForecast = ({
  id,
  days,
  intervalHours,
  corrected,
  kbygQueryParams,
  units,
  noCache = false,
}: FetchWeatherForecastParams) => {
  let cacheEnabled = !!units?.temperature;
  const headers: HeadersInit = {};
  if (noCache === true) {
    headers['Cache-Control'] = 'no-cache';
    cacheEnabled = false;
  }
  return baseFetch(
    `/kbyg/spots/forecasts/weather?${createParamString({
      ...kbygQueryParams,
      spotId: id,
      days,
      intervalHours,
      corrected,
      cacheEnabled,
      'units[temperature]': units?.temperature ?? null,
    })}`,
    {
      headers,
    },
  );
};

export const fetchSunlightTimes = ({
  id,
  days,
  kbygQueryParams,
  intervalHours,
  startDate,
}: FetchSunlightTimesParams) =>
  baseFetch(
    `/kbyg/spots/forecasts/sunlight?${createParamString({
      ...kbygQueryParams,
      spotId: id,
      days,
      intervalHours,
      start: startDate,
    })}`,
  );

export const fetchWindForecast = ({
  id,
  days,
  intervalHours,
  kbygQueryParams,
  units,
  correctedWind = false,
  noCache = false,
  startDate,
}: FetchWindForecastParams) => {
  const headers: HeadersInit = {};
  let cacheEnabled = !!units?.windSpeed;
  if (noCache === true) {
    headers['Cache-Control'] = 'no-cache';
    cacheEnabled = false;
  }
  return baseFetch(
    `/kbyg/spots/forecasts/wind?${createParamString({
      ...kbygQueryParams,
      spotId: id,
      days,
      intervalHours,
      corrected: correctedWind,
      cacheEnabled,
      'units[windSpeed]': units?.windSpeed ?? null,
      start: startDate,
    })}`,
    {
      headers,
    },
  );
};

export const fetchRatingForecast = ({
  corrected,
  days,
  id,
  intervalHours,
  kbygQueryParams,
  noCache = false,
  ratingsAlgorithm,
  ratingsEndpoint,
  startDate,
}: FetchRatingForecastParams) => {
  let cacheEnabled = true;
  const headers: HeadersInit = {};
  if (noCache) {
    headers['Cache-Control'] = 'no-cache';
    cacheEnabled = false;
  }
  return baseFetch(
    `/kbyg/spots/forecasts/rating?${createParamString({
      ...kbygQueryParams,
      spotId: id,
      days,
      intervalHours,
      ratingsAlgorithm,
      ratingsEndpoint,
      corrected,
      start: startDate,
      cacheEnabled,
    })}`,
    {
      headers,
    },
  );
};

export const fetchRegionalConditionForecast = ({
  id,
  days,
  corrected,
  noCache = false,
  startDate,
}: FetchRegionalConditionForecastParams) => {
  const headers: HeadersInit = {};
  if (noCache === true) {
    headers['Cache-Control'] = 'no-cache';
  }
  return baseFetch(
    `/kbyg/regions/forecasts/conditions?${createParamString({
      subregionId: id,
      days,
      corrected,
      start: startDate,
    })}`,
    {
      headers,
    },
  );
};

export const fetchTaxonomySwellEvents = ({ id, type }: FetchTaxonomySwellEventsParams) =>
  baseFetch(`/kbyg/events/taxonomy?${createParamString({ id, type })}`);

export const fetchSwellEventById = ({
  id,
  cookies,
  isServer = false,
}: {
  id: string;
  cookies: Record<string, string>;
  isServer?: boolean;
}): Promise<{
  event: {
    updates: { content: string }[];
  };
}> =>
  baseFetch(`/feed/events/${id}`, {
    cookies,
    headers: { ...addCustomUserAgentHeader(isServer) },
  });

export const fetchConsistencyForecast = ({
  cameraId,
  days,
  id,
  intervalHours,
  kbygQueryParams,
  noCache = false,
  waveCountEndpoint,
}: FetchConsistencyForecastParams) => {
  const headers: HeadersInit = {};
  if (noCache) {
    headers['Cache-Control'] = 'no-cache';
  }
  return baseFetch(
    `/kbyg/spots/forecasts/consistency?${createParamString({
      ...kbygQueryParams,
      cameraId,
      days,
      intervalHours,
      spotId: id,
      waveCountEndpoint,
    })}`,
    {
      headers,
    },
  );
};

export const fetchSwellSpectraForecast = ({
  days,
  id,
  intervalHours,
  kbygQueryParams,
  startDate,
  noCache = false,
}: FetchSwellSpectraForecastParams) => {
  const headers: HeadersInit = {};
  if (noCache) {
    headers['Cache-Control'] = 'no-cache';
  }
  return baseFetch(
    `/kbyg/spots/forecasts/spectra?${createParamString({
      ...kbygQueryParams,
      days,
      intervalHours,
      spotId: id,
      start: startDate,
    })}`,
    {
      headers,
    },
  );
};

export const fetchEnergyForecast = ({
  corrected,
  days,
  id,
  intervalHours,
  kbygQueryParams,
  noCache = false,
  startDate,
}: FetchEnergyForecastParams) => {
  const headers: HeadersInit = {};
  let cacheEnabled = true;
  if (noCache === true) {
    headers['Cache-Control'] = 'no-cache';
    cacheEnabled = false;
  }

  return baseFetch(
    `/kbyg/spots/forecasts/energy?${createParamString({
      ...kbygQueryParams,
      cacheEnabled,
      corrected,
      days,
      intervalHours,
      spotId: id,
      start: startDate,
    })}`,
    {
      headers,
    },
  );
};

export const fetchCrowdForecast = async ({
  cameraId,
  days = 1,
  intervalHours = 1,
  lineupEndpoint,
  noCache = false,
  ratingsAlgorithm,
  spotId,
  start,
}: FetchCrowdForecastParams): Promise<FetchCrowdForecastResponse> => {
  const headers: HeadersInit = {};
  if (noCache) {
    headers['Cache-Control'] = 'no-cache';
  }
  return baseFetch(
    `/kbyg/spots/forecasts/crowd?${createParamString({
      cameraId,
      days,
      intervalHours,
      lineupEndpoint,
      ratingsAlgorithm,
      spotId,
      start,
    })}`,
    {
      headers,
    },
  );
};

export interface GetRateTheSurfParams {
  spotId: string;
  noCache?: boolean;
}

export interface GetRateTheSurfResponse {
  clusteringId: string;
  spot: {
    id: string;
    name: string;
  };
  units: Pick<Units, 'swellHeight' | 'tideHeight' | 'waveHeight' | 'windSpeed'>;
  samples: RateSurfSample[];
}

export const getRateTheSurf = async ({
  spotId,
  noCache = false,
}: GetRateTheSurfParams): Promise<GetRateTheSurfResponse> => {
  const headers: HeadersInit = {};
  if (noCache) {
    headers['Cache-Control'] = 'no-cache';
  }
  return baseFetch(
    `/kbyg/rate-the-surf?${createParamString({
      spotId,
    })}`,
    {
      headers,
    },
  );
};

export interface PostRateTheSurfParams {
  spotId: string;
  rating: number | null;
  timestamp: number;
  clusteringId?: string | null;
}

export interface PostRateTheSurfResponse {
  message: string;
  rating: {
    userId: string;
    spotId: string;
    rating: number;
    timestamp: string;
    createdAt: string;
  };
}

export const postRateTheSurf = async ({
  spotId,
  rating,
  timestamp,
  clusteringId,
}: PostRateTheSurfParams): Promise<PostRateTheSurfResponse> => {
  const accessToken = getAccessToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(accessToken ? { 'X-Auth-AccessToken': accessToken } : {}),
  };
  return baseFetch('/kbyg/rate-the-surf?', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      spotId,
      rating,
      timestamp,
      clusteringId: clusteringId ?? null,
    }),
  });
};
